"use client";

import { useEffect, useState } from "react";
import { useAnalytics } from "@/lib/analytics/hooks";

type Locale = "en" | "id";

/** Sole switch for the CTA. Enabled = shown; absent/disabled = nothing renders. */
export const JOB_ASK_AI_FLAG = "job-ask-ai-cta";

function t(locale: Locale, en: string, id: string): string {
	return locale === "en" ? en : id;
}

export function buildAskAiPrompt(opts: {
	locale: Locale;
	title: string;
	company: string;
	jobUrl: string;
}): string {
	const { locale, title, company, jobUrl } = opts;
	return t(
		locale,
		`Help me decide if I should apply to "${title}" at ${company}: ${jobUrl}. Summarize requirements, red flags, and how to tailor my CV.`,
		`Bantu saya memutuskan apakah sebaiknya melamar "${title}" di ${company}: ${jobUrl}. Ringkas persyaratan, tanda bahaya, dan cara menyesuaikan CV saya.`,
	);
}

const LINK_CLASS =
	"rounded text-xs font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

export function AskAiLinks({
	jobId,
	jobUrl,
	title,
	company,
	locale,
}: {
	jobId: string;
	jobUrl: string;
	title: string;
	company: string;
	locale: Locale;
}) {
	const { posthog } = useAnalytics();
	const [enabled, setEnabled] = useState<boolean | null>(null);
	useEffect(() => {
		if (!posthog) return;
		const check = () =>
			setEnabled(posthog.getFeatureFlag(JOB_ASK_AI_FLAG) === true);
		check();
		return posthog.onFeatureFlags(check);
	}, [posthog]);
	if (enabled !== true) return null;

	const q = encodeURIComponent(
		buildAskAiPrompt({ locale, title, company, jobUrl }),
	);
	const track = (target: "chatgpt" | "claude") =>
		posthog?.capture("job_ask_ai_clicked", { target, job_id: jobId });

	return (
		<div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 pb-1">
			<span className="text-xs text-muted-foreground">
				{t(locale, "Not sure about this job?", "Ragu dengan lowongan ini?")}
			</span>
			<a
				href={`https://chatgpt.com/?q=${q}`}
				target="_blank"
				rel="noopener"
				className={LINK_CLASS}
				onClick={() => track("chatgpt")}
			>
				{t(locale, "Ask ChatGPT", "Tanya ChatGPT")}
			</a>
			<a
				href={`https://claude.ai/new?q=${q}`}
				target="_blank"
				rel="noopener"
				className={LINK_CLASS}
				onClick={() => track("claude")}
			>
				{t(locale, "Ask Claude", "Tanya Claude")}
			</a>
		</div>
	);
}
